import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Download,
  Zap,
  Flame,
  Euro,
  Wifi,
  User
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATI_PRATICA = [
  { value: 'bozza', label: 'Bozza', color: 'bg-slate-100 text-slate-700' },
  { value: 'documenti_richiesti', label: 'Documenti richiesti', color: 'bg-amber-100 text-amber-700' },
  { value: 'documenti_ricevuti', label: 'Documenti ricevuti', color: 'bg-sky-100 text-sky-700' },
  { value: 'in_lavorazione', label: 'In lavorazione', color: 'bg-violet-100 text-violet-700' },
  { value: 'inviata', label: 'Inviata', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'inserita_portale', label: 'Inserita nel portale', color: 'bg-blue-100 text-blue-700' },
  { value: 'accettata', label: 'Accettata', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'attiva', label: 'Attiva', color: 'bg-green-100 text-green-700' },
  { value: 'pagabile', label: 'Pagabile', color: 'bg-teal-100 text-teal-700' },
  { value: 'scartata', label: 'Scartata', color: 'bg-red-100 text-red-700' },
  { value: 'annullata', label: 'Annullata', color: 'bg-red-100 text-red-700' },
];

const TIPOLOGIE = [
  { value: 'luce', label: 'Luce', icon: Zap, color: 'text-amber-500' },
  { value: 'gas', label: 'Gas', icon: Flame, color: 'text-sky-500' },
  { value: 'dual', label: 'Dual', icon: null, color: '' },
  { value: 'internet', label: 'Internet/Fibra', icon: Wifi, color: 'text-violet-500' },
];

const TIPI_OPERAZIONE = [
  { value: 'switch', label: 'Switch', color: 'bg-sky-100 text-sky-700' },
  { value: 'voltura_switch', label: 'Voltura con Switch', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'posa_contatore', label: 'Posa Contatore', color: 'bg-amber-100 text-amber-700' },
  { value: 'subentro', label: 'Subentro', color: 'bg-violet-100 text-violet-700' },
  { value: 'nuovo_allaccio', label: 'Nuovo Allaccio', color: 'bg-emerald-100 text-emerald-700' },
];

const TipoOperazioneBadge = ({ value }) => {
  if (!value) return null;
  const op = TIPI_OPERAZIONE.find(o => o.value === value);
  if (!op) return null;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium ${op.color}`}>
      {op.label}
    </span>
  );
};

const TipologiaIcon = ({ tipologia }) => {
  if (tipologia === 'luce') return <Zap className="w-4 h-4 text-amber-500 shrink-0" />;
  if (tipologia === 'gas') return <Flame className="w-4 h-4 text-sky-500 shrink-0" />;
  if (tipologia === 'internet') return <Wifi className="w-4 h-4 text-violet-500 shrink-0" />;
  if (tipologia === 'dual') {
    return (
      <div className="flex shrink-0">
        <Zap className="w-3.5 h-3.5 text-amber-500" />
        <Flame className="w-3.5 h-3.5 text-sky-500 -ml-0.5" />
      </div>
    );
  }
  return null;
};

const MobileContractCard = ({ contract, navigate, hasRole, onDelete, getStatoStyle, getStatoLabel, formatDate, formatCurrency }) => (
  <div
    className="px-4 py-3 active:bg-slate-50 dark:active:bg-slate-800 cursor-pointer"
    onClick={() => navigate(`/contracts/${contract.id}`)}
    data-testid={`contract-card-${contract.id}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <TipologiaIcon tipologia={contract.tipologia} />
        <p className="font-medium text-slate-900 dark:text-slate-100 truncate text-sm">
          {contract.cliente_nome || '-'}
        </p>
      </div>
      <div className="flex items-start gap-1 shrink-0">
        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatoStyle(contract.stato)}`}>
          {getStatoLabel(contract.stato)}
        </span>
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-6 w-6" data-testid={`contract-card-actions-${contract.id}`}>
                <MoreHorizontal className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/contracts/${contract.id}`)}>
                <Eye className="w-4 h-4 mr-2" /> Visualizza
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/contracts/${contract.id}/edit`)}>
                <Edit className="w-4 h-4 mr-2" /> Modifica
              </DropdownMenuItem>
              {hasRole(['super_admin', 'admin']) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onDelete(contract.id)} className="text-red-600">
                    <Trash2 className="w-4 h-4 mr-2" /> Elimina
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
    {contract.tipo_operazione && (
      <div className="mt-1 ml-6">
        <TipoOperazioneBadge value={contract.tipo_operazione} />
      </div>
    )}
    {(contract.pod || contract.pdr) && (
      <div className="mt-1.5 ml-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
        {contract.pod && (
          <span className="flex items-center gap-1"><Zap className="w-3 h-3 text-amber-500" /><span className="font-mono">{contract.pod}</span></span>
        )}
        {contract.pdr && (
          <span className="flex items-center gap-1"><Flame className="w-3 h-3 text-sky-500" /><span className="font-mono">{contract.pdr}</span></span>
        )}
      </div>
    )}
    <div className="mt-1.5 ml-6 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 truncate">
      <span className="truncate font-medium">{contract.fornitore || '-'}</span>
      {contract.offerta && <><span className="text-slate-300">•</span><span className="truncate">{contract.offerta}</span></>}
    </div>
    <div className="mt-1 ml-6 flex items-center justify-between gap-2 text-[11px] text-slate-500 dark:text-slate-400">
      <span className="truncate">{contract.sub_agente_nome || '-'}</span>
      <div className="flex items-center gap-2 shrink-0">
        {!hasRole(['interno', 'back_office']) && contract.commissione_maturata ? (
          <span className="font-semibold text-emerald-600 tabular-nums">{formatCurrency(contract.commissione_maturata)}</span>
        ) : null}
        <span className="tabular-nums">{formatDate(contract.created_at)}</span>
      </div>
    </div>
  </div>
);

const Contracts = () => {
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    stato: searchParams.get('stato') || '',
    tipologia: '',
    sub_agente_id: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [groupByClient, setGroupByClient] = useState(true);
  const [newContract, setNewContract] = useState({
    cliente_id: searchParams.get('cliente_id') || '',
    tipologia: 'luce',
    tipo_operazione: '',
    pod: '',
    pdr: '',
    fornitore: '',
    offerta: '',
    stato: 'bozza',
    sub_agente_id: '',
    note_operative: '',
    commissione_prevista: '',
    commissione_agenzia: '',
    metodo_pagamento: 'rid',
    tipo_bolletta: 'web',
    iban: '',
    // Internet-specific fields
    codice_cliente_internet: '',
    tecnologia: '',
    velocita_download: '',
    velocita_upload: '',
    prezzo_mensile: '',
    modem_incluso: false,
    costo_attivazione: '',
    indirizzo_installazione: ''
  });

  const { hasRole } = useAuth();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetchContracts();
    fetchClients();
    fetchUsers();
    fetchCompanies();
  }, [filters]);

  // Auto-open create modal if URL has cliente_id param
  useEffect(() => {
    const clienteIdParam = searchParams.get('cliente_id');
    if (clienteIdParam) {
      setShowCreateModal(true);
      setNewContract(prev => ({ ...prev, cliente_id: clienteIdParam }));
    }
  }, [searchParams]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/companies?attiva=true`);
      setCompanies(response.data);
    } catch (error) {}
  };

  const fetchContracts = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(`${API_URL}/api/contracts?${params}`);
      setContracts(response.data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error('Errore nel caricamento delle pratiche');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data.filter(u => ['sub_agent', 'admin', 'super_admin'].includes(u.ruolo)));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreateContract = async () => {
    try {
      // Coerce empty strings to null for numeric fields (Pydantic rejects empty strings on int/float).
      const toNum = (v) => (v === '' || v === null || v === undefined ? null : (Number.isNaN(parseFloat(v)) ? null : parseFloat(v)));
      const toInt = (v) => (v === '' || v === null || v === undefined ? null : (Number.isNaN(parseInt(v, 10)) ? null : parseInt(v, 10)));

      const payload = {
        ...newContract,
        tipo_operazione: newContract.tipo_operazione || null,
        sub_agente_id: newContract.sub_agente_id || null,
        commissione_prevista: toNum(newContract.commissione_prevista),
        commissione_agenzia: toNum(newContract.commissione_agenzia),
        // Internet-specific numerics
        velocita_download: toInt(newContract.velocita_download),
        velocita_upload: toInt(newContract.velocita_upload),
        prezzo_mensile: toNum(newContract.prezzo_mensile),
        costo_attivazione: toNum(newContract.costo_attivazione),
      };

      // Strip internet-specific fields when tipologia is not 'internet' to keep payload clean
      if (payload.tipologia !== 'internet') {
        ['codice_cliente_internet', 'tecnologia', 'velocita_download', 'velocita_upload', 'prezzo_mensile', 'modem_incluso', 'costo_attivazione', 'indirizzo_installazione'].forEach(k => {
          delete payload[k];
        });
      }

      await axios.post(`${API_URL}/api/contracts`, payload);
      toast.success('Pratica creata con successo');
      setShowCreateModal(false);
      setNewContract({
        cliente_id: '',
        tipologia: 'luce',
        tipo_operazione: '',
        pod: '',
        pdr: '',
        fornitore: '',
        offerta: '',
        stato: 'bozza',
        sub_agente_id: '',
        note_operative: '',
        commissione_prevista: '',
        commissione_agenzia: '',
        metodo_pagamento: 'rid',
        tipo_bolletta: 'web',
        iban: '',
        codice_cliente_internet: '',
        tecnologia: '',
        velocita_download: '',
        velocita_upload: '',
        prezzo_mensile: '',
        modem_incluso: false,
        costo_attivazione: '',
        indirizzo_installazione: ''
      });
      fetchContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione della pratica');
    }
  };

  const handleDeleteContract = async (contractId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa pratica?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/contracts/${contractId}`);
      toast.success('Pratica eliminata');
      fetchContracts();
    } catch (error) {
      toast.error('Errore nell\'eliminazione della pratica');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/export/contracts`);
      const blob = new Blob([response.data.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      toast.success('Export completato');
    } catch (error) {
      toast.error('Errore nell\'export');
    }
  };

  const getStatoStyle = (stato) => {
    return STATI_PRATICA.find(s => s.value === stato)?.color || 'bg-slate-100 text-slate-700';
  };

  const getStatoLabel = (stato) => {
    return STATI_PRATICA.find(s => s.value === stato)?.label || stato;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
  };

  // Search filter
  const filteredContracts = contracts.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    const clientMatch = clients.find(cl => cl.id === c.cliente_id);
    const cf = clientMatch?.codice_fiscale?.toLowerCase() || '';
    const nome = (c.cliente_nome || '').toLowerCase();
    return nome.includes(q) || cf.includes(q);
  });

  // Group by client
  const groupedContracts = (() => {
    if (!groupByClient) return null;
    const groups = {};
    filteredContracts.forEach(c => {
      const key = c.cliente_id || 'senza_cliente';
      if (!groups[key]) {
        const clientMatch = clients.find(cl => cl.id === c.cliente_id);
        groups[key] = {
          cliente_id: c.cliente_id,
          cliente_nome: c.cliente_nome || 'Senza Cliente',
          codice_fiscale: clientMatch?.codice_fiscale || '',
          contracts: []
        };
      }
      groups[key].contracts.push(c);
    });
    return Object.values(groups).sort((a, b) => {
      const lastA = a.contracts[0]?.created_at || '';
      const lastB = b.contracts[0]?.created_at || '';
      return lastB.localeCompare(lastA);
    });
  })();

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-full max-w-md" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contracts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Pratiche</h1>
          <p className="text-slate-500 mt-1">{contracts.length} pratiche totali</p>
        </div>
        <div className="flex items-center gap-3">
          {hasRole(['super_admin', 'admin']) && (
            <Button variant="outline" onClick={handleExport} data-testid="export-contracts-btn">
              <Download className="w-4 h-4 mr-2" />
              Esporta
            </Button>
          )}
          <Button onClick={() => setShowCreateModal(true)} data-testid="create-contract-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuova Pratica
          </Button>
        </div>
      </div>

      {/* Search + Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            <div className="flex-1">
              <Input 
                placeholder="Cerca per nome cliente o codice fiscale..." 
                value={search} 
                onChange={(e) => setSearch(e.target.value)}
                data-testid="contracts-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                className={`px-3 py-1.5 text-xs rounded-full font-medium transition-colors ${groupByClient ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                onClick={() => setGroupByClient(!groupByClient)}
                data-testid="group-by-client-toggle"
              >
                {groupByClient ? 'Raggruppati per cliente' : 'Lista piatta'}
              </button>
              <Button 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? 'bg-slate-100' : ''}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtri
              </Button>
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
              <Select 
                value={filters.stato || "all"} 
                onValueChange={(v) => setFilters({...filters, stato: v === "all" ? "" : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Stato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  {STATI_PRATICA.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select 
                value={filters.tipologia || "all"} 
                onValueChange={(v) => setFilters({...filters, tipologia: v === "all" ? "" : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tipologia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  {TIPOLOGIE.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {hasRole(['super_admin', 'admin']) && (
                <Select 
                  value={filters.sub_agente_id || "all"} 
                  onValueChange={(v) => setFilters({...filters, sub_agente_id: v === "all" ? "" : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Agente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contracts - Desktop Table + Mobile Cards */}
      <Card>
        <CardContent className="p-0">
          {/* Desktop / tablet table (>= 640px) */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="table-header">
                  <th className="text-left px-4 py-3">Tipologia</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3">POD / PDR</th>
                  <th className="text-left px-4 py-3">Fornitore / Offerta</th>
                  <th className="text-left px-4 py-3">Stato</th>
                  <th className="text-left px-4 py-3">Agente</th>
                  {!hasRole(['interno', 'back_office']) && <th className="text-right px-4 py-3">Commissione Sub-Agente</th>}
                  {!hasRole(['interno', 'back_office', 'sub_agent']) && <th className="text-right px-4 py-3">Comm. Agenzia</th>}
                  <th className="text-left px-4 py-3">Data</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredContracts.length === 0 ? (
                  <tr>
                    <td colSpan={hasRole(['interno', 'back_office']) ? 8 : hasRole(['sub_agent']) ? 9 : 10} className="px-4 py-12 text-center text-slate-500">
                      Nessuna pratica trovata
                    </td>
                  </tr>
                ) : groupByClient && groupedContracts ? (
                  groupedContracts.map((group) => (
                    <React.Fragment key={group.cliente_id}>
                      {/* Client header row */}
                      <tr className="bg-slate-50/80 border-t-2 border-slate-200">
                        <td colSpan={hasRole(['interno', 'back_office']) ? 8 : hasRole(['sub_agent']) ? 9 : 10} className="px-4 py-2">
                          <div className="flex items-center gap-3">
                            <User className="w-4 h-4 text-slate-500" />
                            <span className="font-semibold text-slate-800">{group.cliente_nome}</span>
                            {group.codice_fiscale && (
                              <span className="text-xs text-slate-400 font-mono bg-slate-100 px-2 py-0.5 rounded">{group.codice_fiscale}</span>
                            )}
                            <Badge variant="outline" className="text-[10px]">{group.contracts.length} {group.contracts.length === 1 ? 'pratica' : 'pratiche'}</Badge>
                            <Button variant="ghost" size="sm" className="h-6 text-xs ml-auto"
                              onClick={(e) => { e.stopPropagation(); navigate(`/contracts?cliente_id=${group.cliente_id}`); }}
                              data-testid={`add-contract-client-${group.cliente_id}`}>
                              <Plus className="w-3 h-3 mr-1" />Aggiungi
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {/* Contract rows for this client */}
                      {group.contracts.map((contract) => (
                        <tr 
                          key={contract.id} 
                          className="table-row cursor-pointer hover:bg-sky-50/30"
                          onClick={() => navigate(`/contracts/${contract.id}`)}
                          data-testid={`contract-row-${contract.id}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                {contract.tipologia === 'luce' ? (
                                  <Zap className="w-5 h-5 text-amber-500" />
                                ) : contract.tipologia === 'gas' ? (
                                  <Flame className="w-5 h-5 text-sky-500" />
                                ) : contract.tipologia === 'internet' ? (
                                  <Wifi className="w-5 h-5 text-violet-500" />
                                ) : (
                                  <div className="flex">
                                    <Zap className="w-4 h-4 text-amber-500" />
                                    <Flame className="w-4 h-4 text-sky-500 -ml-1" />
                                  </div>
                                )}
                                <span className="font-medium capitalize">{contract.tipologia}</span>
                              </div>
                              <TipoOperazioneBadge value={contract.tipo_operazione} />
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-900">{contract.cliente_nome || '-'}</p>
                          </td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              {contract.pod && (
                                <div className="flex items-center gap-1 text-sm">
                                  <Zap className="w-3 h-3 text-amber-500" />
                                  <span className="font-mono text-xs">{contract.pod}</span>
                                </div>
                              )}
                              {contract.pdr && (
                                <div className="flex items-center gap-1 text-sm">
                                  <Flame className="w-3 h-3 text-sky-500" />
                                  <span className="font-mono text-xs">{contract.pdr}</span>
                                </div>
                              )}
                              {!contract.pod && !contract.pdr && <span className="text-slate-400 text-sm">-</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-slate-900">{contract.fornitore || '-'}</p>
                              <p className="text-sm text-slate-500">{contract.offerta || '-'}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatoStyle(contract.stato)}`}>
                              {getStatoLabel(contract.stato)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {contract.sub_agente_nome || '-'}
                          </td>
                          {!hasRole(['interno', 'back_office']) && (
                            <td className="px-4 py-3 text-right">
                              <div className="text-sm">
                                {contract.commissione_maturata ? (
                                  <span className="font-semibold text-emerald-600 tabular-nums">
                                    {formatCurrency(contract.commissione_maturata)}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </div>
                            </td>
                          )}
                          {!hasRole(['interno', 'back_office', 'sub_agent']) && (
                            <td className="px-4 py-3 text-right">
                              <div className="text-sm">
                                {contract.commissione_agenzia ? (
                                  <span className="font-semibold text-sky-600 tabular-nums">
                                    {formatCurrency(contract.commissione_agenzia)}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </div>
                            </td>
                          )}
                          <td className="px-4 py-3 text-sm text-slate-500 tabular-nums">
                            {formatDate(contract.created_at)}
                          </td>
                          <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/contracts/${contract.id}`)}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Visualizza
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate(`/contracts/${contract.id}/edit`)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Modifica
                                </DropdownMenuItem>
                                {hasRole(['super_admin', 'admin']) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                      onClick={() => handleDeleteContract(contract.id)}
                                      className="text-red-600"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Elimina
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))
                ) : (
                  filteredContracts.map((contract) => (
                    <tr 
                      key={contract.id} 
                      className="table-row cursor-pointer"
                      onClick={() => navigate(`/contracts/${contract.id}`)}
                      data-testid={`contract-row-${contract.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            {contract.tipologia === 'luce' ? (
                              <Zap className="w-5 h-5 text-amber-500" />
                            ) : contract.tipologia === 'gas' ? (
                              <Flame className="w-5 h-5 text-sky-500" />
                            ) : (
                              <div className="flex">
                                <Zap className="w-4 h-4 text-amber-500" />
                                <Flame className="w-4 h-4 text-sky-500 -ml-1" />
                              </div>
                            )}
                            <span className="font-medium capitalize">{contract.tipologia}</span>
                          </div>
                          <TipoOperazioneBadge value={contract.tipo_operazione} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{contract.cliente_nome || '-'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          {contract.pod && (
                            <div className="flex items-center gap-1 text-sm">
                              <Zap className="w-3 h-3 text-amber-500" />
                              <span className="font-mono text-xs">{contract.pod}</span>
                            </div>
                          )}
                          {contract.pdr && (
                            <div className="flex items-center gap-1 text-sm">
                              <Flame className="w-3 h-3 text-sky-500" />
                              <span className="font-mono text-xs">{contract.pdr}</span>
                            </div>
                          )}
                          {!contract.pod && !contract.pdr && <span className="text-slate-400 text-sm">-</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900">{contract.fornitore || '-'}</p>
                          <p className="text-sm text-slate-500">{contract.offerta || '-'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatoStyle(contract.stato)}`}>
                          {getStatoLabel(contract.stato)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {contract.sub_agente_nome || '-'}
                      </td>
                      {!hasRole(['interno', 'back_office']) && (
                        <td className="px-4 py-3 text-right">
                          <div className="text-sm">
                            {contract.commissione_maturata ? (
                              <span className="font-semibold text-emerald-600 tabular-nums">
                                {formatCurrency(contract.commissione_maturata)}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </div>
                        </td>
                      )}
                      {!hasRole(['interno', 'back_office', 'sub_agent']) && (
                        <td className="px-4 py-3 text-right">
                          <div className="text-sm">
                            {contract.commissione_agenzia ? (
                              <span className="font-semibold text-sky-600 tabular-nums">
                                {formatCurrency(contract.commissione_agenzia)}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm text-slate-500 tabular-nums">
                        {formatDate(contract.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/contracts/${contract.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Visualizza
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/contracts/${contract.id}/edit`)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Modifica
                            </DropdownMenuItem>
                            {hasRole(['super_admin', 'admin']) && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteContract(contract.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Elimina
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards (< 640px) */}
          <div className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
            {filteredContracts.length === 0 ? (
              <p className="px-4 py-12 text-center text-slate-500">Nessuna pratica trovata</p>
            ) : groupByClient && groupedContracts ? (
              groupedContracts.map((group) => (
                <div key={group.cliente_id}>
                  {/* Client group header */}
                  <div className="bg-slate-50 dark:bg-slate-800/50 px-4 py-2 flex items-center gap-2 border-t border-slate-200 dark:border-slate-700">
                    <User className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                    <span className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate flex-1">{group.cliente_nome}</span>
                    <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">{group.contracts.length}</Badge>
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/contracts?cliente_id=${group.cliente_id}`); }}
                      className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500"
                      title="Aggiungi pratica"
                      data-testid={`mobile-add-contract-client-${group.cliente_id}`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {group.contracts.map((contract) => (
                    <MobileContractCard
                      key={contract.id}
                      contract={contract}
                      navigate={navigate}
                      hasRole={hasRole}
                      onDelete={handleDeleteContract}
                      getStatoStyle={getStatoStyle}
                      getStatoLabel={getStatoLabel}
                      formatDate={formatDate}
                      formatCurrency={formatCurrency}
                    />
                  ))}
                </div>
              ))
            ) : (
              filteredContracts.map((contract) => (
                <MobileContractCard
                  key={contract.id}
                  contract={contract}
                  navigate={navigate}
                  hasRole={hasRole}
                  onDelete={handleDeleteContract}
                  getStatoStyle={getStatoStyle}
                  getStatoLabel={getStatoLabel}
                  formatDate={formatDate}
                  formatCurrency={formatCurrency}
                />
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Contract Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova Pratica</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <Select 
                value={newContract.cliente_id || undefined} 
                onValueChange={(v) => {
                  const selectedClient = clients.find(c => c.id === v);
                  setNewContract({
                    ...newContract, 
                    cliente_id: v,
                    pod: selectedClient?.pod || newContract.pod,
                    pdr: selectedClient?.pdr || newContract.pdr
                  });
                }}
              >
                <SelectTrigger data-testid="new-contract-cliente">
                  <SelectValue placeholder="Seleziona cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.tipo_cliente === 'business' && c.ragione_sociale 
                        ? c.ragione_sociale 
                        : `${c.nome} ${c.cognome}`}
                      {c.codice_fiscale && <span className="ml-2 text-slate-400 font-mono text-[10px]">CF: {c.codice_fiscale}</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipologia *</Label>
              <Select 
                value={newContract.tipologia} 
                onValueChange={(v) => setNewContract({...newContract, tipologia: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOLOGIE.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(newContract.tipologia === 'luce' || newContract.tipologia === 'gas' || newContract.tipologia === 'dual') && (
              <div className="space-y-2">
                <Label>Tipo Operazione</Label>
                <Select
                  value={newContract.tipo_operazione || 'none'}
                  onValueChange={(v) => setNewContract({...newContract, tipo_operazione: v === 'none' ? '' : v})}
                >
                  <SelectTrigger data-testid="new-contract-tipo-operazione">
                    <SelectValue placeholder="Seleziona tipo operazione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non specificato</SelectItem>
                    {TIPI_OPERAZIONE.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {(newContract.tipologia === 'luce' || newContract.tipologia === 'dual') && (
              <div className="space-y-2">
                <Label>POD (Punto di Prelievo)</Label>
                <Input
                  value={newContract.pod}
                  onChange={(e) => setNewContract({...newContract, pod: e.target.value})}
                  placeholder="IT001E12345678"
                  data-testid="new-contract-pod"
                />
              </div>
            )}
            {(newContract.tipologia === 'gas' || newContract.tipologia === 'dual') && (
              <div className="space-y-2">
                <Label>PDR (Punto di Riconsegna)</Label>
                <Input
                  value={newContract.pdr}
                  onChange={(e) => setNewContract({...newContract, pdr: e.target.value})}
                  placeholder="12345678901234"
                  data-testid="new-contract-pdr"
                />
              </div>
            )}
            {newContract.tipologia === 'internet' && (
              <div className="space-y-3 bg-violet-50 rounded-lg p-4 border border-violet-200">
                <p className="text-sm font-semibold text-violet-700">Dettagli Internet/Fibra</p>
                <div className="space-y-2">
                  <Label>Codice Cliente Internet</Label>
                  <Input
                    value={newContract.codice_cliente_internet}
                    onChange={(e) => setNewContract({...newContract, codice_cliente_internet: e.target.value})}
                    placeholder="Codice contratto internet"
                    data-testid="new-contract-codice-internet"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Tecnologia</Label>
                    <Select value={newContract.tecnologia || 'FTTH'} onValueChange={(v) => setNewContract({...newContract, tecnologia: v})}>
                      <SelectTrigger data-testid="new-contract-tecnologia"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FTTH">FTTH</SelectItem>
                        <SelectItem value="FTTC">FTTC</SelectItem>
                        <SelectItem value="FWA">FWA</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Prezzo Mensile (EUR)</Label>
                    <Input type="number" step="0.01"
                      value={newContract.prezzo_mensile}
                      onChange={(e) => setNewContract({...newContract, prezzo_mensile: e.target.value})}
                      placeholder="19.90"
                      data-testid="new-contract-prezzo-mensile"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Download (Mbps)</Label>
                    <Input type="number" value={newContract.velocita_download}
                      onChange={(e) => setNewContract({...newContract, velocita_download: e.target.value})}
                      placeholder="1000" />
                  </div>
                  <div className="space-y-2">
                    <Label>Upload (Mbps)</Label>
                    <Input type="number" value={newContract.velocita_upload}
                      onChange={(e) => setNewContract({...newContract, velocita_upload: e.target.value})}
                      placeholder="300" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Costo Attivazione (EUR)</Label>
                    <Input type="number" step="0.01" value={newContract.costo_attivazione}
                      onChange={(e) => setNewContract({...newContract, costo_attivazione: e.target.value})}
                      placeholder="0" />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <input type="checkbox" checked={newContract.modem_incluso}
                      onChange={(e) => setNewContract({...newContract, modem_incluso: e.target.checked})}
                      className="rounded border-slate-300" data-testid="new-contract-modem" />
                    <Label>Modem Incluso</Label>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Indirizzo Installazione</Label>
                  <Input
                    value={newContract.indirizzo_installazione}
                    onChange={(e) => setNewContract({...newContract, indirizzo_installazione: e.target.value})}
                    placeholder="Via Roma 1, 20100 Milano"
                    data-testid="new-contract-indirizzo"
                  />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Fornitore</Label>
              <Select 
                value={newContract.fornitore || undefined} 
                onValueChange={(v) => setNewContract({...newContract, fornitore: v, offerta: ''})}
              >
                <SelectTrigger data-testid="new-contract-fornitore">
                  <SelectValue placeholder="Seleziona fornitore" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={c.nome}>{c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Offerta</Label>
              {(() => {
                const selectedCompany = companies.find(c => c.nome === newContract.fornitore);
                const offerte = selectedCompany?.offerte?.filter(o => 
                  o.attiva && (o.tipologia === newContract.tipologia || o.tipologia === 'dual')
                ) || [];
                return offerte.length > 0 ? (
                  <Select 
                    value={newContract.offerta || undefined} 
                    onValueChange={(v) => {
                      const off = offerte.find(o => o.nome === v);
                      setNewContract({
                        ...newContract, 
                        offerta: v,
                        commissione_prevista: off?.commissione_agente || newContract.commissione_prevista
                      });
                    }}
                  >
                    <SelectTrigger data-testid="new-contract-offerta">
                      <SelectValue placeholder="Seleziona offerta" />
                    </SelectTrigger>
                    <SelectContent>
                      {offerte.map(o => (
                        <SelectItem key={o.id} value={o.nome}>
                          {o.nome} {o.commissione_agente ? `(€${o.commissione_agente})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={newContract.offerta}
                    onChange={(e) => setNewContract({...newContract, offerta: e.target.value})}
                    placeholder={newContract.fornitore ? "Nessuna offerta disponibile" : "Seleziona prima un fornitore"}
                  />
                );
              })()}
            </div>
            {hasRole(['super_admin', 'admin']) && (
              <div className="space-y-2">
                <Label>Assegna a</Label>
                <Select 
                  value={newContract.sub_agente_id || "none"} 
                  onValueChange={(v) => setNewContract({...newContract, sub_agente_id: v === "none" ? "" : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona agente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non assegnato</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!hasRole(['interno', 'back_office', 'sub_agent']) && (
              <div className="space-y-2">
                <Label>Commissione Sub-Agente (EUR)</Label>
                <Input
                  type="number"
                  value={newContract.commissione_prevista}
                  onChange={(e) => setNewContract({...newContract, commissione_prevista: e.target.value})}
                  placeholder="0.00"
                />
              </div>
            )}
            {!hasRole(['interno', 'back_office', 'sub_agent']) && (
              <div className="space-y-2">
                <Label>Commissione Agenzia (EUR)</Label>
                <Input
                  type="number"
                  value={newContract.commissione_agenzia || ''}
                  onChange={(e) => setNewContract({...newContract, commissione_agenzia: e.target.value})}
                  placeholder="0.00"
                />
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Metodo Pagamento *</Label>
                <Select value={newContract.metodo_pagamento} onValueChange={(v) => setNewContract({...newContract, metodo_pagamento: v, iban: v !== 'rid' ? '' : newContract.iban})}>
                  <SelectTrigger data-testid="new-contract-pagamento"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rid">RID Bancario</SelectItem>
                    <SelectItem value="bollettino">Bollettino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipo Bolletta *</Label>
                <Select value={newContract.tipo_bolletta} onValueChange={(v) => setNewContract({...newContract, tipo_bolletta: v})}>
                  <SelectTrigger data-testid="new-contract-bolletta"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="web">Bolletta Web</SelectItem>
                    <SelectItem value="cartacea">Bolletta Cartacea</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {newContract.metodo_pagamento === 'rid' && (
              <div className="space-y-2">
                <Label>IBAN</Label>
                <Input
                  value={newContract.iban}
                  onChange={(e) => setNewContract({...newContract, iban: e.target.value.toUpperCase().replace(/\s/g, '')})}
                  placeholder="IT60X0542811101000000123456"
                  data-testid="new-contract-iban"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Note Operative</Label>
              <textarea
                className="w-full h-20 px-3 py-2 text-sm border rounded-md border-slate-300"
                value={newContract.note_operative}
                onChange={(e) => setNewContract({...newContract, note_operative: e.target.value})}
                placeholder="Note aggiuntive..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleCreateContract}
              disabled={!newContract.cliente_id}
              data-testid="submit-new-contract-btn"
            >
              Crea Pratica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contracts;
